import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { getUniqueTacticGroupTitle } from '../TacticKanban.utils';
import { TacticStatus } from 'basics/enums/maya.enums';
import { useBulkUpdateTacticMutation } from 'basics/graphql/mutations/bulkUpdateTactic';
import { useCreateTacticGroupMutation } from 'basics/graphql/mutations/createTacticGroup';
import { useUpdateApprovedTacticMutation } from 'basics/graphql/mutations/updateApprovedTactic';
import useGetCepByIdLazyQuery from 'basics/graphql/queries/getCepByIdLazy';
import { tacticGroupToBE, tacticToBulkUpdateInput, tacticToTacticInput } from 'basics/transformers/Tactic.transformer';
import { CepStateType } from 'basics/types/maya.types';
import { succesToast } from 'basics/utils/toast';
import { defaultTacticVersion } from 'components/TacticForm/TacticForm.constants';
import { Tactic, TacticInput } from 'generated/maya.types';
import cepState from 'states/cep.states';

type UseTacticKanbanMutationsParams = {
  cepId: string | null;
  sendToVeeva: boolean;
  isApprovedCEP: boolean;
  refetchAllTactics: () => Promise<unknown>;
};

const useTacticKanbanMutations = ({
  cepId,
  sendToVeeva,
  isApprovedCEP,
  refetchAllTactics,
}: UseTacticKanbanMutationsParams) => {
  const { t } = useTranslation();
  const cepStateValue = useSnapshot(cepState) as CepStateType;

  const [bulkUpdateTactic] = useBulkUpdateTacticMutation();
  const [updateApprovedTactic] = useUpdateApprovedTacticMutation();
  const [createTacticGroup] = useCreateTacticGroupMutation();
  const { queryGetCepById } = useGetCepByIdLazyQuery();

  const executeBulkUpdate = async (bulkUpdateInputs: ReturnType<typeof tacticToBulkUpdateInput>[]) => {
    if (bulkUpdateInputs.length === 0) {
      return;
    }

    const { error } = await bulkUpdateTactic(cepId as string, bulkUpdateInputs, sendToVeeva);

    if (error) {
      throw new Error(error);
    }
  };

  const updateGroupRelationsOnApprovedCEP = async (tacticsToUpdate: Tactic[]) => {
    try {
      return tacticsToUpdate.reduce<Promise<boolean>>(async (prevPromise, tactic) => {
        const prevHasError = await prevPromise;

        if (prevHasError) {
          return true;
        }

        const originalTactic = cepStateValue.tactics.find((item) => item._id === tactic._id);

        const isMajorUpdates = !!originalTactic && (
          tactic.actionOrder !== originalTactic.actionOrder
                    || tactic.mandatory !== originalTactic.mandatory
        );

        if (!cepId) {
          throw new Error(t('errors_standard'));
        }

        const tacticInput: Partial<TacticInput> = tacticToTacticInput(tactic, cepId);

        if (tacticInput.version) {
          tacticInput.version += 1;
        } else {
          tacticInput.version = defaultTacticVersion + 1;
        }

        const { error } = await updateApprovedTactic(
          tactic._id,
          tacticInput,
          cepId,
          isMajorUpdates,
          sendToVeeva,
        );

        return !!error;
      }, Promise.resolve(false));
    } catch (error) {
      return true;
    }
  };

  const updateGroupRelations = async (newTactics: Tactic[], originalTactics?: Tactic[]) => {
    const tacticsToUpdateMap = new Map<string, Tactic>();

    newTactics.forEach((newTactic) => {
      const original = originalTactics?.find((tactic) => tactic._id === newTactic._id);

      if (!original) {
        return;
      }

      const hasChanged = newTactic.actionOrder !== original.actionOrder
          || (newTactic.parentTactic?._id || null) !== (original.parentTactic?._id || null)
          || (newTactic.previousTactic?._id || null) !== (original.previousTactic?._id || null)
          || newTactic.mandatory !== original.mandatory;

      if (hasChanged) {
        tacticsToUpdateMap.set(newTactic._id, newTactic);
      }
    });

    const tacticsToUpdate = Array.from(tacticsToUpdateMap.values());

    if (isApprovedCEP) {
      return updateGroupRelationsOnApprovedCEP(tacticsToUpdate);
    }

    try {
      await executeBulkUpdate(
        tacticsToUpdate.map((tactic) => tacticToBulkUpdateInput(tactic, cepId as string)),
      );

      return false;
    } catch (error) {
      return true;
    }
  };

  const syncAfterMutations = async (successMessage: string) => {
    await refetchAllTactics();

    const { data: cep } = await queryGetCepById({
      variables: {
        id: cepStateValue.cep?._id,
      },
    });

    if (cepState.cep && cep?.getCustomerEngagementPlanningById) {
      cepState.cep.globalCepSyncStatus = cep.getCustomerEngagementPlanningById.globalCepSyncStatus;
    }

    succesToast(successMessage);
  };

  const handleTacticGroupCreation = async (tacticIdsToGroup: string[], groupIdToReuse?: string | null) => {
    if (!cepId) {
      throw new Error(t('errors_standard'));
    }

    if (!isApprovedCEP && groupIdToReuse) {
      const releasedGroup = cepState.tacticGroups.find((group) => group._id === groupIdToReuse);

      if (releasedGroup) {
        return releasedGroup;
      }
    }

    if (!isApprovedCEP) {
      const usedGroupIds = new Set<string>();

      cepState.tactics.forEach((tactic) => {
        if (tactic.parentTactic?._id) {
          usedGroupIds.add(tactic.parentTactic._id);
        }
      });

      const freeGroup = cepState.tacticGroups.find((group) => !usedGroupIds.has(group._id));

      if (freeGroup) {
        return freeGroup;
      }
    }

    const groupTitles = cepStateValue.tacticGroups.map((group) => group.title);
    const newTitle = getUniqueTacticGroupTitle({
      groupTitles,
      groupNamePrefix: t('maya_tactic_form_group_name'),
    });

    const tacticGroupStatus = TacticStatus.APPROVED;

    const formattedTacticGroup = tacticGroupToBE(
      newTitle,
      cepId as string,
      defaultTacticVersion,
      tacticGroupStatus,
    );

    const syncToVeeva = isApprovedCEP;

    const tacticIdsToAttachOnCreate = isApprovedCEP
      ? []
      : tacticIdsToGroup;

    const { data: createdGroup, error: createError } = await createTacticGroup(
      formattedTacticGroup,
      cepId as string,
      tacticIdsToAttachOnCreate,
      syncToVeeva,
    );

    if (createError || !createdGroup) {
      throw new Error(createError || 'Failed to create tactic group.');
    }

    return createdGroup;
  };

  return {
    updateGroupRelations,
    syncAfterMutations,
    handleTacticGroupCreation,
  };
};

export default useTacticKanbanMutations;
